import { useEffect, useState } from "react";
import { Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { StatusBadge, thCls, tdCls } from "./ui";

const STATUSES = ["new", "contacted", "quoted", "invoiced", "paid", "converted", "declined"];

export default function AdRequests() {
  const [reqs, setReqs] = useState(null);

  const load = () => api.get("/admin/ad-requests").then((r) => setReqs(r.data || [])).catch(() => setReqs([]));
  useEffect(() => { load(); }, []);

  const patch = async (id, body, msg) => {
    await api.patch(`/admin/ad-requests/${id}`, body).catch((e) => toast.error(formatApiError(e)));
    toast.success(msg);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    await api.delete(`/admin/ad-requests/${id}`).catch(() => {});
    load();
  };

  return (
    <div data-testid="admin-ad-requests">
      <div className="flex items-center gap-3 mb-6 bg-gold-light border border-gold/40 rounded-xl px-5 py-4">
        <Receipt className="w-5 h-5 text-gold-dark shrink-0" />
        <p className="text-sm text-clay">
          Workflow: contact the advertiser → set the agreed amount → mark <strong>invoiced</strong> after sending the
          QuickBooks invoice → mark <strong>paid</strong> when settled → convert to a live campaign in Ads & Campaigns.
        </p>
      </div>
      <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm overflow-x-auto">
        <table className="w-full text-left min-w-[900px]" data-testid="ad-requests-table">
          <thead className="border-b border-sand">
            <tr>
              <th className={thCls}>Business</th><th className={thCls}>Contact</th><th className={thCls}>Placement</th>
              <th className={thCls}>Dates</th><th className={thCls}>Budget</th><th className={thCls}>Amount ($)</th><th className={thCls}>Status</th><th className={thCls}></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sand">
            {(reqs || []).map((r) => (
              <tr key={r.id} data-testid={`ad-request-row-${r.id}`}>
                <td className={tdCls}>
                  <p className="font-semibold">{r.business_name}</p>
                  <p className="text-xs text-stone2">{r.ad_type.replace(/_/g, " ")}{r.category ? ` · ${r.category}` : ""}</p>
                </td>
                <td className={tdCls}>
                  <p className="text-sm">{r.contact_name}</p>
                  <a href={`mailto:${r.email}`} className="text-xs text-ruby">{r.email}</a>
                </td>
                <td className={`${tdCls} font-mono text-xs`}>{r.placement}</td>
                <td className={`${tdCls} text-xs`}>{r.start_date || "—"} → {r.end_date || "—"}</td>
                <td className={`${tdCls} text-xs`}>{r.budget || "—"}</td>
                <td className={tdCls}>
                  <input
                    type="number"
                    defaultValue={r.amount || 0}
                    onBlur={(e) => patch(r.id, { amount: Number(e.target.value) || 0 }, "Amount saved.")}
                    className="w-24 px-2.5 py-1.5 rounded-lg bg-sand/60 text-sm outline-none border border-transparent focus:border-gold"
                    data-testid={`request-amount-${r.id}`}
                  />
                </td>
                <td className={tdCls}>
                  <select
                    value={r.status}
                    onChange={(e) => patch(r.id, { status: e.target.value }, "Status updated.")}
                    className="px-2 py-1.5 rounded-lg bg-sand/60 text-xs font-semibold outline-none"
                    data-testid={`request-status-${r.id}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className={tdCls}>
                  <button onClick={() => remove(r.id)} className="p-2 text-ruby hover:bg-ruby-light rounded-lg" data-testid={`delete-request-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {reqs && reqs.length === 0 && <p className="px-5 py-10 text-sm text-stone2 text-center">No advertising requests yet.</p>}
      </div>
    </div>
  );
}
